/**
 * CSS embarqué du SDK widget (modes popup + badge — Phase 3).
 *
 * Toutes les classes sont préfixées `.rtt-rb-` (Rintintin Booking) pour éviter
 * les collisions avec le CSS du site hôte. On n'utilise pas Shadow DOM
 * (compatibilité ancienne) ; à la place, sélecteurs très spécifiques + reset
 * léger sur les éléments racines.
 *
 * Z-index :
 *  - overlay popup = 2147483647 (max int 32 signé)
 *  - badge        = 2147483600 (au-dessus de tout sauf l'overlay)
 *
 * `ensureStylesInjected(doc)` est idempotent : on injecte un seul `<style id="rtt-rb-styles">`
 * et on évite tout reflow inutile en cas d'appels multiples (popup + badge sur
 * la même page).
 */

/** ID unique du tag style injecté dans le `<head>` du document hôte. */
export const RTT_RB_STYLES_ID = 'rtt-rb-styles';

/**
 * Reset minimaliste appliqué aux éléments racines (overlay, badge).
 * On évite `all: revert` qui peut casser des choses dans certains navigateurs
 * — un reset explicite est plus prévisible.
 */
const RESET = `
  box-sizing: border-box;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.4;
  color: #0f172a;
  margin: 0;
  padding: 0;
`;

/** CSS as string — sera inliné via un tag `<style>`. Pas de template fancy. */
export const RTT_RB_CSS = `
.rtt-rb-popup-overlay {
  ${RESET}
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  z-index: 2147483647;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.rtt-rb-popup-overlay.rtt-rb-popup-overlay--open {
  display: flex;
}
.rtt-rb-popup-frame {
  ${RESET}
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
}
.rtt-rb-popup-frame.rtt-rb-popup-frame--checkout {
  max-width: 640px;
}
.rtt-rb-popup-close {
  ${RESET}
  position: absolute;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
}
.rtt-rb-popup-close:hover {
  background: rgba(15, 23, 42, 0.16);
}
.rtt-rb-popup-close:focus-visible {
  outline: 2px solid #a5765c;
  outline-offset: 2px;
}
.rtt-rb-popup-iframe {
  display: block;
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  border: 0;
  background: #ffffff;
}
.rtt-rb-trigger {
  ${RESET}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 0;
  border-radius: 999px;
  background: #a5765c;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18);
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.rtt-rb-trigger:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.22);
}
.rtt-rb-trigger:focus-visible {
  outline: 2px solid var(--rtt-trigger-accent, #a5765c);
  outline-offset: 2px;
}
.rtt-rb-badge {
  ${RESET}
  position: fixed;
  bottom: 24px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: 0;
  border-radius: 999px;
  background: #a5765c;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.22);
  z-index: 2147483600;
  animation: rtt-rb-pulse 1.5s ease-in-out infinite;
}
.rtt-rb-badge:hover {
  filter: brightness(1.05);
}
.rtt-rb-badge:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
}
.rtt-rb-badge--bottom-right {
  right: 24px;
}
.rtt-rb-badge--bottom-left {
  left: 24px;
}
.rtt-rb-badge-icon {
  display: inline-flex;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
.rtt-rb-badge-icon svg {
  width: 100%;
  height: 100%;
  display: block;
}
.rtt-rb-badge-label {
  display: inline-block;
  white-space: nowrap;
}
@keyframes rtt-rb-pulse {
  0%   { transform: scale(1); }
  50%  { transform: scale(1.04); }
  100% { transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .rtt-rb-badge {
    animation: none;
  }
  .rtt-rb-trigger {
    transition: none;
  }
  .rtt-rb-popup-close {
    transition: none;
  }
}
`;

/**
 * Injecte les styles dans le `<head>` du document si pas déjà présents.
 * Idempotent — si un `<style id="rtt-rb-styles">` existe déjà, on ne touche
 * à rien (un autre mount du SDK l'a déjà fait).
 */
export function ensureStylesInjected(doc: Document): void {
  if (!doc || !doc.head) return;
  if (doc.getElementById(RTT_RB_STYLES_ID)) return;
  const style = doc.createElement('style');
  style.id = RTT_RB_STYLES_ID;
  style.type = 'text/css';
  style.appendChild(doc.createTextNode(RTT_RB_CSS));
  doc.head.appendChild(style);
}
